import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getOrders } from "../../../helpers/urlConfig/apiFunctions";
import DeleteComponent from "../../../Components/Common/deleteComponent/DeleteComponent";
import { dateChangerFunc, dateParser } from "../../../helpers/dateParser";
import OrderCreate from "./OrderCreate";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import CustomerCreate from "../Customers/CustomerCreate";
import { documentTitle } from "../../../helpers/documentTitle";
import errorResponseHandler from "../../../helpers/errorResponseHandler";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const OrderList = () => {
  const navigate = useNavigate();
  const [order_modal_list, set_order_modal_list] = useState(false);
  const [modal_delete, setmodal_delete] = useState(false);
  const [edit_modal_list, set_edit_modal_list] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [deleteId, setDeleteProductId] = useState(null);

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const order_tog_list = () => set_order_modal_list(!order_modal_list);
  const edit_tog_list = () => set_edit_modal_list(!edit_modal_list);
  const tog_delete = () => setmodal_delete(!modal_delete);

  /** Add Customer Options */
  const [customer_modal_list, set_customer_modal_list] = useState(false);
  const customer_tog_list = () => set_customer_modal_list(!customer_modal_list);

  documentTitle("Sales");

  const fetchData = async () => {
    try {
      const response = await getOrders();

      if (response.statusCode === 200) {
        setOrders(response.data);
        setFilteredOrders(response.data);

        // Set customer options for dropdown
        const uniqueCustomers = [
          ...new Set(response.data.map((order) => order.customer_name)),
        ];
        setCustomerOptions(
          uniqueCustomers.map((customer) => ({
            label: customer,
            value: customer,
          }))
        );
      } else {
        errorResponseHandler(response);
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditClick = (product) => {
    setCurrentProduct(product);
    set_edit_modal_list(true);
  };

  const onClickRemoveBtn = (id) => {
    setDeleteProductId(id);
    tog_delete();
  };

  const navigateToOrderDetails = (order) => {
    let orderId = `${order.order_id}-${order.customer_name}`;
    navigate(`/order-product/${orderId}`);
  };

  // Filter by customer
  const handleCustomerChange = (selectedOption) => {
    setSelectedCustomer(selectedOption);

    let filtered = orders;

    if (selectedOption) {
      filtered = orders.filter(
        (order) => order.customer_name === selectedOption.value
      );
    }

    setFilteredOrders(filtered);
  };

  // Apply button handler for date range filtering
  const handleApplyDateFilter = () => {
    let filtered;

    if (selectedCustomer) {
      filtered = orders.filter(
        (order) => order.customer_name === selectedCustomer.value
      );
    }

    filtered = filterByDate(filtered || orders);
    setFilteredOrders(filtered);
  };

  const filterByDate = (data) => {
    const parsedFromDate = fromDate ? fromDate : null;
    const parsedToDate = toDate ? toDate : null;

    return data.filter((order) => {
      const orderDate = dateParser(order.created_at);
      return (
        (!parsedFromDate || orderDate >= parsedFromDate) &&
        (!parsedToDate || orderDate <= parsedToDate)
      );
    });
  };

  const clearDateFilters = () => {
    setFromDate("");
    setToDate("");
  };

  // Export to Excel
  const exportToExcel = () => {
    const data = filteredOrders.map((order, index) => ({
      "SL.No": index + 1,
      "Order ID": order.order_id,
      "Customer Name": order.customer_name,
      "Created At": dateChangerFunc(dateParser(order.created_at)),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SalesOrders");

    XLSX.writeFile(workbook, `SalesOrders_${new Date().toISOString()}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = ["SL.No", "Order ID", "Customer Name", "Created At"];
    const tableRows = [];

    filteredOrders.forEach((order, index) => {
      const row = [
        index + 1,
        order.order_id,
        order.customer_name,
        dateChangerFunc(dateParser(order.created_at)),
      ];
      tableRows.push(row);
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    doc.save(`SalesOrders_${new Date().toISOString()}.pdf`);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Sales List"
            pageTitle="Sales"
            exportToExcel={exportToExcel}
            exportToPDF={exportToPDF}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id="customerList">
                    <Row className="g-2 mb-3 align-items-end">
                      {/* Add Orders Button */}
                      <Col sm={4} md={3} lg={2}>
                        <Button
                          color="success"
                          className="add-btn"
                          onClick={order_tog_list}
                          id="create-btn"
                        >
                          <i className="ri-add-line align-bottom"></i>Add
                        </Button>
                      </Col>

                      {/* Customer Name Filter */}
                      <Col sm={6} md={4} lg={3}>
                        <div>
                          <label className="form-label">Customer Name</label>
                          <div className="d-flex gap-2">
                            <Select
                              options={customerOptions}
                              isClearable
                              value={selectedCustomer}
                              onChange={handleCustomerChange}
                              placeholder="Select Customer"
                              className="flex-grow-1"
                            />
                            <Button color="success" onClick={customer_tog_list}>
                              <i className="ri-add-line"></i>
                            </Button>
                          </div>
                        </div>
                      </Col>

                      {/* From Date Filter */}
                      <Col sm={6} md={2} lg={2}>
                        <div>
                          <label className="form-label">From Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>
                      </Col>

                      {/* To Date Filter */}
                      <Col sm={6} md={4} lg={2}>
                        <div>
                          <label className="form-label">To Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                      </Col>

                      {/* Apply Filter Button */}
                      <Col sm={6} md={2} lg={1}>
                        <Button
                          color="primary"
                          className="w-100"
                          onClick={handleApplyDateFilter}
                        >
                          Apply
                        </Button>
                      </Col>
                      <Col sm={6} md={2} lg={1}>
                        <Button
                          color="secondary"
                          onClick={clearDateFilters}
                          disabled={!fromDate && !toDate}
                        >
                          Clear
                        </Button>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <table className="table align-middle table-nowrap">
                        <thead className="table-light">
                          <tr>
                            <th>SL.No</th>
                            <th>Order ID</th>
                            <th>Customer Name</th>
                            <th>Created At</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredOrders &&
                            filteredOrders.map((order, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>
                                  <button
                                    className="btn btn-link"
                                    onClick={() =>
                                      navigateToOrderDetails(order)
                                    }
                                  >
                                    {order.order_id}
                                  </button>
                                </td>
                                <td>{order.customer_name}</td>
                                <td>
                                  {dateChangerFunc(
                                    dateParser(order.created_at)
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    {/* <div className="edit">
                                      <button
                                        className="btn btn-sm btn-success"
                                        onClick={() => handleEditClick(order)}
                                      >
                                        Edit
                                      </button>
                                    </div> */}
                                    <div className="remove">
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() =>
                                          onClickRemoveBtn(order.order_id)
                                        }
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      {filteredOrders.length === 0 && (
                        <div className="text-center mt-4">
                          <p>No Order Found.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <OrderCreate
        order_modal_list={order_modal_list}
        set_order_modal_list={set_order_modal_list}
        order_tog_list={order_tog_list}
        fetchData={fetchData}
      />

      <CustomerCreate
        customer_modal_list={customer_modal_list}
        set_customer_modal_list={set_customer_modal_list}
        customer_tog_list={customer_tog_list}
        fetchData={fetchData}
      />

      <DeleteComponent
        modal_delete={modal_delete}
        tog_delete={tog_delete}
        fetchData={fetchData}
        deleteId={deleteId}
        keyName="Order"
      />

      <ToastContainer />
    </React.Fragment>
  );
};

export default OrderList;
